import { useEffect } from 'react';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import type { OnboardingSystem } from '../pages/CatalogPage';

interface UseOnboardingDataOptions {
  selectedPlatform: string | null;
  currentView: string;
}

const PLATFORMS_WITH_ONBOARDING = [
  'Shift4 Dine',
  'Shift4 Venue',
  'Integrated Payments',
  'Shift4 Duo',
  'Shift4 One',
];

function mapPlatformName(platform: string): string {
  switch (platform) {
    case 'Integrated Payments':
      return '3PI';
    case 'Shift4 Dine':
      return 'Skytab';
    case 'Shift4 Venue':
      return 'Skytab Venue';
    case 'Shift4 Duo':
      return 'Shift4 Mobile';
    default:
      return platform;
  }
}

/**
 * Fetches onboarding systems for the selected platform and writes them
 * into ProductMatrixContext.
 */
export function useOnboardingData({
  selectedPlatform,
  currentView,
}: UseOnboardingDataOptions): void {
  const { setOnboardingSystems, setOnboardingColumnGroups } = useProductMatrix();

  useEffect(() => {
    const fetchOnboardingData = async () => {
      if (
        !selectedPlatform ||
        !PLATFORMS_WITH_ONBOARDING.includes(selectedPlatform) ||
        currentView !== 'platform'
      ) {
        setOnboardingSystems([]);
        setOnboardingColumnGroups(null);
        return;
      }

      try {
        const onboardingPlatformName = mapPlatformName(selectedPlatform);
        const response = await fetch('/api/onboarding');
        const result = await response.json();

        if (result.success) {
          const filteredSystems = result.data.filter(
            (system: OnboardingSystem) =>
              system.platform.toLowerCase() === onboardingPlatformName.toLowerCase()
          );
          setOnboardingSystems(filteredSystems);
          setOnboardingColumnGroups(result.columnGroups);
        }
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
        setOnboardingSystems([]);
        setOnboardingColumnGroups(null);
      }
    };

    fetchOnboardingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, currentView]);
}
